import { Formik, FormikConfig, FormikHelpers, FormikProps } from "formik"
import PropTypes from "prop-types"
import React, { forwardRef, useImperativeHandle, useRef } from "react"
import DiscardFormStateContext from "./DiscardFormStateContext"
import { DiscardFormStateReturn } from "./useDiscardFormState"
import useFormikDiscardFormState, {
  FormikDiscardFormStateOptions
} from "./useFormikDiscardFormState"

type DiscardRef = Pick<DiscardFormStateReturn, "discardFormState">

type DiscardFormStateChildren<T extends any = any> =
  | React.ReactNode
  | ((props: DiscardFormStateReturn & T) => React.ReactNode)

type FormikWithDiscardFormStateProps = {
  children: DiscardFormStateChildren<FormikProps<any>>
  // We'll discard stored form state on submit, but how should the current form state be handled?
  // Either preserve it or reset it to initial values. Preserve when submitting the form results in
  // the same data being stored in the DB, reset when submitting the form triggers some action
  // (like sending an email).
  preserveFormStateOnSubmit?: boolean
} & FormikDiscardFormStateOptions &
  FormikConfig<any>

export const FormikWithDiscardFormState = forwardRef<
  DiscardRef,
  FormikWithDiscardFormStateProps
>(function FormikWithDiscardFormState(
  {
    children,
    keys,
    formStateKey,
    localStorageOptions,
    onChangeDiscardFormState,
    preserveFormStateOnSubmit = true,
    // The rest are formikProps.
    onSubmit,
    ...formikProps
  },
  ref
) {
  const internalRef = useRef<DiscardRef>({} as DiscardRef)

  // Expose internalRef to the outside ref, if provided.
  useImperativeHandle(ref, () => internalRef.current!, [])

  // Wrap the onSubmit hander to discard form state after submit.
  async function handleSubmit(values: any, formikHelpers: FormikHelpers<any>) {
    try {
      const result = onSubmit(values, formikHelpers)
      // Reset form state after submit.
      internalRef.current.discardFormState(!!preserveFormStateOnSubmit)
      return result
    } catch (error) {
      throw error
    }
  }

  return (
    <Formik {...formikProps} onSubmit={handleSubmit}>
      {(form) => (
        <FormikDiscardFormState
          ref={internalRef}
          keys={keys}
          formStateKey={formStateKey}
          localStorageOptions={localStorageOptions}
          onChangeDiscardFormState={onChangeDiscardFormState}
        >
          {(formikFormDiscardState) =>
            typeof children === "function"
              ? children({
                  ...form,
                  ...formikFormDiscardState
                })
              : children
          }
        </FormikDiscardFormState>
      )}
    </Formik>
  )
})

type FormikDiscardFormStateProps = {
  children: DiscardFormStateChildren
} & FormikDiscardFormStateOptions

const FormikDiscardFormState = forwardRef<
  DiscardRef,
  FormikDiscardFormStateProps
>(function FormikDiscardFormState(
  { children, ...formikDiscardFormStateProps },
  ref
) {
  const formikFormDiscardState = useFormikDiscardFormState(
    formikDiscardFormStateProps
  )

  useImperativeHandle(ref, () => ({
    discardFormState: formikFormDiscardState.discardFormState
  }))

  return (
    <DiscardFormStateContext.Provider value={formikFormDiscardState}>
      {typeof children === "function"
        ? children(formikFormDiscardState)
        : children}
    </DiscardFormStateContext.Provider>
  )
})

FormikDiscardFormState.displayName = "FormikDiscardFormState"

FormikDiscardFormState.propTypes = {
  children: PropTypes.func.isRequired
}

export default FormikDiscardFormState
